import type { ConsentCheckboxKey } from "@instacover/helpers"
import { Button, Heading, Text } from "@instacover/ui"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useCallback, useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"

import { ClientRedirect } from "@/components/ClientRedirect"
import { Checkbox } from "@/components/form/inputs/Checkbox"
import { InstructionImage } from "@/components/icons/InstructionImage"
import { ExtraIcons } from "@/components/icons/InstructionImageType"
import { BaseLayout } from "@/components/layouts/BaseLayout"
import { SpinnerLayout } from "@/components/layouts/SpinnerLayout"
import { IconArrowRight } from "@/images/xd/icons/IconArrowRight"
import { LINKS } from "@/routing/links"
import { useFinishStepAndNavigate, useStepsRouting } from "@/routing/useStepsRouting"
import { markConsentScreenSeen, useSessionDetail } from "@/services/dexie"
import { useGuardedAction } from "@/utils/hooks/useGuardedAction"

export const Route = createFileRoute("/$sessionId/_session/consent")({
  component: Consent,
})

function Consent() {
  const { sessionId } = Route.useParams()
  const { t } = useTranslation()

  const { firstUnfinishedStepLink, isLoading } = useStepsRouting(sessionId)
  const { data: sessionDetail, isLoading: isLoadingDetail } = useSessionDetail(sessionId)
  const finishStepAndNavigate = useFinishStepAndNavigate(sessionId)

  const [checkedKeys, setCheckedKeys] = useState<Set<ConsentCheckboxKey>>(() => new Set())

  // Details cached before the field existed have it undefined, which means no checkboxes
  const checkboxes = useMemo(() => sessionDetail?.consentCheckboxes ?? [], [sessionDetail?.consentCheckboxes])
  const canContinue = useMemo(() => checkboxes.every(({ key }) => checkedKeys.has(key)), [checkboxes, checkedKeys])

  const toggleCheckbox = useCallback((key: ConsentCheckboxKey, checked: boolean) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])

  const onContinue = useCallback(async () => {
    // Record locally first so it works offline; the BackgroundUploader syncs it to the backend
    await markConsentScreenSeen(sessionId)
    await finishStepAndNavigate("GDPRConsent")
  }, [sessionId, finishStepAndNavigate])

  const closingText = t("consent.closingText")

  const transComponents = useMemo(
    () => ({
      p: <p className="not-last:mb-4" />,
      br: <br />,
      a: <a href={t("consent.linkUrl")} target="_blank" rel="noreferrer" className="text-ic-primary underline" />,
      terms: <a href={t("consent.termsUrl")} target="_blank" rel="noreferrer" className="text-ic-primary underline" />,
      privacyNotice: (
        <a
          href={t("consent.privacyNoticeUrl")}
          target="_blank"
          rel="noreferrer"
          className="text-ic-primary underline"
        />
      ),
      privacy: <Link to={LINKS.privacyPolicy(sessionId)} className="text-ic-primary underline" />,
    }),
    [t, sessionId],
  )

  // Prevents rage-clicks and disables the button while continuing
  const [runContinue, isContinuing] = useGuardedAction(onContinue)

  if (isLoading || isLoadingDetail) return <SpinnerLayout />

  // Consent already confirmed (e.g. back navigation) or disabled — skip to the next unfinished step
  if (firstUnfinishedStepLink !== LINKS.consent(sessionId)) {
    return <ClientRedirect to={firstUnfinishedStepLink ?? LINKS.finish(sessionId)} />
  }

  return (
    <BaseLayout hideCounter dataTestId="consent">
      <div className="h-vh-header flex flex-col items-center justify-between">
        <div className="text-ic-primary flex h-fit w-[90vw] max-w-[100vw] min-w-0 flex-1 items-center py-6 sm:w-[80vw] sm:max-w-[500px]">
          <InstructionImage
            iconName={ExtraIcons.PhotoAndUnderwriting}
            animated
            className="flex h-full w-full items-center justify-center"
          />
        </div>

        <div className="w-full max-w-120 px-6">
          <Heading as="h2" variant="h1">
            {t("consent.title")}
          </Heading>

          <div className="h-6 w-6 shrink-0" />

          <Text as="div">
            <Trans defaults={t("consent.text")} components={transComponents} />
          </Text>

          <div className="h-6 w-6 shrink-0" />

          {checkboxes.length > 0 && (
            <>
              <div className="flex flex-col gap-4">
                {checkboxes.map(({ key }) => (
                  <Checkbox
                    key={key}
                    checked={checkedKeys.has(key)}
                    onCheckedChange={(checked) => toggleCheckbox(key, checked)}
                  >
                    <Text as="span">
                      <Trans defaults={t(`consent.checkboxes.${key}`)} components={transComponents} />
                    </Text>
                  </Checkbox>
                ))}
              </div>

              <div className="h-6 w-6 shrink-0" />
            </>
          )}

          {closingText && (
            <>
              <Text as="div">{closingText}</Text>

              <div className="h-6 w-6 shrink-0" />
            </>
          )}

          <div className="flex flex-row items-center justify-end">
            <Button
              variant="primary"
              shape="circle"
              data-testid="consent-next-btn"
              onClick={runContinue}
              loading={isContinuing}
              disabled={isContinuing || !canContinue}
              icon={<IconArrowRight className="size-8" />}
            />
          </div>

          <div className="h-6 w-6 shrink-0 sm:h-10 sm:w-10" />
        </div>
      </div>
    </BaseLayout>
  )
}
